import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import TextInput from '@components/dataEntry/Input/TextInput';
import PasswordInput from '@components/dataEntry/Input/PasswordInput/index';

import Button from '@components/dataEntry/button/Button';
import { handleEnterKeyPress } from '@utils/handleKeypress.function';
import {
  validateEmailInput,
  validatePasswordValue,
} from '@utils/loginValidate.function';
import { registerWithEmail } from '@store/firebase/user/registerUser';
import { useNavigate } from 'react-router-dom';
import ErrorModal from '@pages/Register/ErrorModal';
import ErrorTypes from '@pages/Register/constants/errorTypes';
import { useInputs } from '@hooks/useInputs';

const Wrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const BtnWrap = styled.div`
  display: flex;
  gap: 16px;
`;
type Props = {
  handleGoBack?: any;
  toggleLogin: () => void;
  setTitle?: boolean;
};

function SignUp({ handleGoBack, toggleLogin, setTitle = true }: Props) {
  const [email, setEmail] = useState('');
  const [{ displayName, requestMessage }, handleChange, reset] = useInputs({
    displayName: '',
    requestMessage: '',
  });
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [password, setPassword] = useState('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [btnDisabled, setBtnDistabled] = useState(true);
  const [errorType, setErrorType] = useState('');
  const ErrorTypeMap = {
    [ErrorTypes.ValidEmail.value]: (
      <ErrorModal
        show={showError}
        handleClose={() => setShowError(!showError)}
        errorType={ErrorTypes.ValidEmail}
      />
    ),
    [ErrorTypes.AlreadyExistEmail.value]: (
      <ErrorModal
        show={showError}
        handleClose={() => setShowError(!showError)}
        errorType={ErrorTypes.AlreadyExistEmail}
      />
    ),
  };

  useEffect(() => {
    setBtnDistabled(
      !(!isEmailValid && !isPasswordValid && email && password && displayName),
    );
  }, [email, password, displayName, isEmailValid, isPasswordValid]);

  const changeEmail = useCallback((e: any) => {
    const value: string = e.target.value;
    const isValid: boolean = validateEmailInput(value);

    setEmail(value);
    setIsEmailValid(!isValid);
  }, []);

  const changePassword = useCallback((e: any) => {
    const value: string = e.target.value;
    // const isValid: boolean = validatePasswordValue(value);
    const isValid: boolean = value.length > 4;
    setPassword(value);
    setIsPasswordValid(!isValid);
  }, []);

  const handleRegister = useCallback(async () => {
    setIsLoading(true);
    setBtnDistabled(true);
    const registerData = { email, password, displayName, requestMessage };

    await registerWithEmail(registerData)
      .then(() => {
        setIsLoading(false);
        handleGoBack();
      })
      .catch((err) => {
        setBtnDistabled(false);
        setIsLoading(false);
        setShowError(true);
        switch (err.code) {
          case 'auth/email-already-in-use':
            setErrorType(ErrorTypes.AlreadyExistEmail.value);
            break;
          case 'auth/invalid-email':
            setErrorType(ErrorTypes.ValidEmail.value);
            break;
        }
      });
  }, [email, password, displayName, requestMessage]);

  return (
    <Wrap>
      {setTitle && <h4>회원가입</h4>}
      <TextInput
        block
        placeholder="닉네임"
        name="displayName"
        value={displayName}
        onChange={handleChange}
      />
      <TextInput
        block
        name="requestMessage"
        placeholder="가입 메시지"
        value={requestMessage}
        onChange={handleChange}
      />
      <TextInput
        block
        name="signUpEmailInput"
        value={email}
        onChange={changeEmail}
        placeholder="이메일 example@example.com"
        validType="email"
        isValid={isEmailValid}
        validMessage="이메일 형식을 올바르게 입력해주세요."
      />
      <PasswordInput
        block
        value={password}
        onChange={changePassword}
        placeholder="비밀번호(영문/숫자/특수문자 혼합 8자 이상)"
        onKeyPress={(e) => handleEnterKeyPress(e, handleRegister)}
        isValid={isPasswordValid}
        validMessage="비밀번호 형식을 올바르게 입력해주세요."
      />
      <BtnWrap>
        {handleGoBack && (
          <Button onClick={handleGoBack} shape="stroke">
            취소
          </Button>
        )}
        <Button
          onClick={handleRegister}
          pending={isLoading}
          disabled={btnDisabled}
        >
          회원가입
        </Button>
      </BtnWrap>
      <Button onClick={toggleLogin} shape="ghost" size="sm">
        로그인
      </Button>

      {ErrorTypeMap[errorType]}
    </Wrap>
  );
}

export default SignUp;
